import type { ISubAccountListState } from '../hooks';

/**
 * V1's status words, unchanged (`SUBACCOUNTSTATUS` in hooks/constants.ts says
 * APPROVED reads "Verified"), mapped onto the chip verdicts the design system
 * already draws — an approval state is a verdict, same as a session's.
 *
 * WHO EACH STATE IS WAITING ON (SIM-55). The chip word alone was read as a
 * fault: an owner saw "UNDER REVIEW" here while the users panel said "Joined",
 * and reported it as stuck. It is not stuck — it is waiting, and on someone
 * other than them. The states map onto two different people:
 *
 * - PENDING — the sub-account's primary user has not finished onboarding.
 *   A different-company sub-account is CREATED here, and clients.api's
 *   business-compliance maps a still-pending KYC/KYB session back to it.
 * - UNDER_REVIEW — onboarding is done; business-compliance maps a `completed`
 *   session here and NEVER to APPROVED. Only a super admin moves it on, via
 *   PATCH /super-admin-business/:id in the super-admin portal. An account in
 *   this state can already sign in (client-login blocks only PENDING and
 *   REJECTED), which is why it looks "active" to the person using it.
 * - REJECTED — the super admin declined it; the primary user resubmits.
 *
 * "Joined" in the users panel is `isVerifiedPhone` and says nothing about any
 * of this, which is why the panel now repeats the account's state.
 */
export interface ISubAccountStatus {
	verdict: 'ok' | 'rev' | 'rej' | 'wait';
	label: string;
	/** Who has to act next, or null once nothing is outstanding. */
	waitingOn: string | null;
	/** One sentence, for the hover and the users panel. */
	explain: string;
}

export const STATUS_CHIP: Record<
	ISubAccountListState['approveStatus'],
	ISubAccountStatus
> = {
	APPROVED: {
		verdict: 'ok',
		label: 'VERIFIED',
		waitingOn: null,
		explain: 'Approved by the super admin. Users can be invited.',
	},
	PENDING: {
		verdict: 'wait',
		label: 'PENDING',
		waitingOn: 'Primary user',
		explain:
			'Waiting on the primary user to accept the invite and complete onboarding.',
	},
	UNDER_REVIEW: {
		verdict: 'rev',
		label: 'UNDER REVIEW',
		waitingOn: 'Super admin',
		explain:
			'Onboarding is complete. Waiting on the super admin to approve the account.',
	},
	REJECTED: {
		verdict: 'rej',
		label: 'VERIFICATION FAILED',
		waitingOn: 'Primary user',
		explain:
			'The super admin did not approve this account. The primary user needs to resubmit.',
	},
};

/** Name or status text, case-insensitive — what the old table's search matched. */
export const search = (
	rows: ISubAccountListState[],
	term: string
): ISubAccountListState[] => {
	const q = term.trim().toLowerCase();
	if (!q) return rows;
	return rows.filter(
		r =>
			(r.name ?? '').toLowerCase().includes(q) ||
			STATUS_CHIP[r.approveStatus]?.label.toLowerCase().includes(q) ||
			(STATUS_CHIP[r.approveStatus]?.waitingOn ?? '').toLowerCase().includes(q)
	);
};

/** The strip: every number derived from the rows already fetched, nothing extra. */
export const summarise = (rows: ISubAccountListState[]) => ({
	total: rows.length,
	verified: rows.filter(r => r.approveStatus === 'APPROVED').length,
	awaiting: rows.filter(
		r => r.approveStatus === 'PENDING' || r.approveStatus === 'UNDER_REVIEW'
	).length,
	/* The same "awaiting" count split by who it is waiting on — the one
	   number this card existed to give, and the one it used to lump together. */
	withPrimaryUser: rows.filter(r => r.approveStatus === 'PENDING').length,
	withSuperAdmin: rows.filter(r => r.approveStatus === 'UNDER_REVIEW').length,
	users: rows.reduce((n, r) => n + (r.totalUsers ?? 0), 0),
});
